// template6 variant（supergol，基于 template4 改）：复刻 supergol.today 顶栏 —— 左 ☰ / 左对齐 logo / 右侧动作区 / 地球语言。
// 与 template4 的区别**只在首页**（pageType=home，个人中心复用同款），子页面沿用 template4 的返回头：
//   · 左侧方形 ☰ 按钮：打开左抽屉（派发 `open-left-drawer` 事件，LeftDrawer 组件监听；也可由 onMenuClick 接管）；
//   · 右侧随登录态切换：未登录=「Entrar」文字按钮 + 「Registro」金色按钮；已登录=余额胶囊（R$ 金额 + 金色存钱罐），
//     整颗胶囊点击直接拉起充值弹层（竞品行为：余额区即充值入口，无账户明细跳转）；
//   · 最右地球图标：语言 Popover（只列后台开启的语言，单语言台子只有一项）。
// 顶栏背景透明（压在 #1b1b19 页面底色上），不随滚动变化。
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useI18n } from "@/i18n/hooks";
import { Button } from "@/components/Base/Button";
import { useCurrencyIcon, useCurrencySymbol } from "@/hooks/useUtils";
import { useUserDataFlattened } from "@/modules";
import {
  usePlatformLogo,
  useEnableDownloadAppFloatingOnHomeTop,
  useLoginRegisterConfig,
} from "@/modules/homeDisplay/hooks";
import { useNavigation, ROUTES } from "@/utils/navigation";
import { openLoginRegisterPopup } from "@/components/LoginRegisterPopup";
import { openDepositPopup } from "@/components/DepositPopup/triggerDepositPopup";
import { useMessageNotification } from "@/modules/notification";
import { DownloadAppBar } from "@/components/DownloadAppBar";
import { isDownloadAppBarSessionClosed } from "@/utils/downloadAppBarSession";
import "./Header.scss";
import type { HeaderProps } from "../../Header.types";
import { utils } from "@/index";
import IconSearch from "@/assets/icons/ic-search.svg";
import IconSupport from "@/assets/icons/support-icon.png";
import IconMessage from "@/assets/icons/message.png";
import IconLanguage from "@/assets/icons/ic-language.svg";
import { useLanguage } from "@/i18n/hooks";
import { getValidLanguages } from "@/i18n";
import { SkinIcon } from "@/components/SkinIcon";

const ICON_URLS = {
  search: IconSearch,
  service: IconSupport,
  message: IconMessage,
  language: IconLanguage,
};

/** 语言 id → 本地化显示名（竞品地球弹层按「português (Brasil)」这种母语名展示）；未登记的回落 id */
const LANGUAGE_LABELS: Record<string, string> = {
  pt: "português (Brasil)",
  en: "English",
  es: "Español",
  ar: "العربية",
  bn: "বাংলা",
  hi: "हिन्दी",
  id: "Bahasa Indonesia",
  vi: "Tiếng Việt",
  zh: "中文",
};

/**
 * 语言面板里的 20px 圆形国旗（竞品用 flag-icons 的 fi-br）。内置巴西 / 美国 / 西班牙三面简化旗，
 * 其余语言回落成带语言码的深色圆点。后台 languageList 没有旗帜字段，暂不可配。
 */
const LanguageFlag: React.FC<{ id: string }> = ({ id }) => {
  const cls = "mobile-header__sg-language-flag";
  if (id === "pt") {
    return (
      <svg className={cls} viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="10" fill="#009b3a" />
        <path d="M10 3.2 17 10l-7 6.8L3 10z" fill="#fedf00" />
        <circle cx="10" cy="10" r="3.6" fill="#002776" />
        <path
          d="M6.6 9.4c2.4-.9 4.8-.6 6.9 1"
          stroke="#fff"
          strokeWidth=".7"
          fill="none"
        />
      </svg>
    );
  }
  if (id === "en") {
    return (
      <svg className={cls} viewBox="0 0 20 20" aria-hidden="true">
        <defs>
          <clipPath id="sg-flag-en">
            <circle cx="10" cy="10" r="10" />
          </clipPath>
        </defs>
        <g clipPath="url(#sg-flag-en)">
          <rect width="20" height="20" fill="#fff" />
          {[0, 2, 4, 6, 8, 10, 12, 14, 16, 18].map(y => (
            <rect key={y} y={y} width="20" height="1.54" fill="#b22234" />
          ))}
          <rect width="9" height="10" fill="#3c3b6e" />
        </g>
      </svg>
    );
  }
  if (id === "es") {
    return (
      <svg className={cls} viewBox="0 0 20 20" aria-hidden="true">
        <defs>
          <clipPath id="sg-flag-es">
            <circle cx="10" cy="10" r="10" />
          </clipPath>
        </defs>
        <g clipPath="url(#sg-flag-es)">
          <rect width="20" height="20" fill="#aa151b" />
          <rect y="5" width="20" height="10" fill="#f1bf00" />
        </g>
      </svg>
    );
  }
  return (
    <span className={`${cls} ${cls}--text`} aria-hidden="true">
      {id.slice(0, 2).toUpperCase()}
    </span>
  );
};

/** 打开左抽屉：Header 与 LeftDrawer 解耦，靠全局事件沟通（LeftDrawer 只在 header=template6 时挂载） */
export const OPEN_LEFT_DRAWER_EVENT = "open-left-drawer";

/** 铃铛描边渐变的 SVG id（同一屏只渲染一个本 variant 的铃铛，固定 id 不会撞） */
const BELL_GRADIENT_ID = "mobile-header-tpl4-bell";

export function Header(props: HeaderProps) {
  const {
    className = "",
    pageType = "home",
    title,
    rightIcon,
    hideBackButton = false,
    onMenuClick,
    onLanguageClick,
    onMessageClick,
    onServiceClick,
    onSearchClick,
    messageUnreadCount: messageUnreadCountProp,
  } = props;

  const { t } = useI18n();
  const routerNavigate = useNavigate();
  const { navigate, isHome } = useNavigation();
  // 余额必须取合并了 realtime 的数据：user_info 拆分后余额只进 realtime 切片，
  // 直接读 state.userInfo 拿不到 loadUserRealtime 的更新，顶部金额会一直不变。
  const { isAuthenticated, data: userInfo } = useUserDataFlattened();
  const headerRef = useRef<HTMLElement>(null);
  const enableFloatingOnHomeTop = useEnableDownloadAppFloatingOnHomeTop();

  // 下载 App 顶栏（DownloadAppBar，position:fixed）在 prime 首页同样挂载（classic 挂载在
  // Header variant 内，prime 此前遗漏 → az-sa / rosetest 等 prime 预设首页看不到下载条）。
  // 组件自带 config / 路由 / 会话关闭三重显隐门；这里只额外跟踪可见态以决定是否渲染等高占位块
  // （__download-slot）——沉浸式浮动顶栏下用它把 header 内容让到下载条之下，与 classic 一致。
  const [downloadAppBarVisible, setDownloadAppBarVisible] = useState(() => {
    if (utils.isApp() || utils.updateCheckIsPwa() || !enableFloatingOnHomeTop)
      return false;
    return !isDownloadAppBarSessionClosed();
  });
  useEffect(() => {
    const handler = (e: Event) =>
      setDownloadAppBarVisible((e as CustomEvent).detail);
    window.addEventListener("downloadAppBarChange", handler);
    return () => window.removeEventListener("downloadAppBarChange", handler);
  }, []);
  const { messageUnreadCount: notificationUnreadCount } =
    useMessageNotification();
  // 与 template3 的差别：不再限定 profile 页 —— 首页铃铛也要显示未读角标
  const messageUnreadCount = messageUnreadCountProp ?? notificationUnreadCount;

  // 用户余额相关（本 variant 无刷新按钮，故不需要 loadUserInfo / 刷新态）
  const currencySymbol = useCurrencySymbol();
  const currencyIcon = useCurrencyIcon();
  // 注册按钮上的首充优惠气泡：文案与样式同登录注册弹窗那颗（同一个配置字段、同一个组件），
  // 但顶栏按钮贴着屏幕顶部，气泡必须朝下，否则会被顶出可视区
  const loginRegisterConfig = useLoginRegisterConfig();
  // 顶栏 / 个人中心的注册按钮气泡走「首页注册按钮优惠文案」；
  // 「注册页首充优惠文案」(registerFirstChargePromoText) 只给注册弹窗与注册页用
  const registerPromoText =
    loginRegisterConfig?.homeRegisterBtnFirstChargeText?.trim() ?? "";

  // 语言 Popover：列后台开启的语言；切换后关闭
  const { language: currentLanguage, changeLanguage } = useLanguage();
  const [languagePopoverVisible, setLanguagePopoverVisible] = useState(false);
  const validLanguages = getValidLanguages();
  // 竞品按语言的本地写法显示（"português (Brasil)"），不显示后台的中文名；内置表没有的语言再回落后台 label / name
  const languageLabel = (id: string) => {
    const cfg = (
      window._config?.languageList as
        | { id: string; name?: string; label?: string }[]
        | undefined
    )?.find(l => l.id === id);
    return LANGUAGE_LABELS[id] || cfg?.label || cfg?.name || id;
  };
  // 语言面板：竞品 ion-popover = 全屏黑 32% 遮罩 + 顶栏下方 1rem、贴右 0.625rem 的深色圆角面板；
  // 遮罩要盖住顶栏本身，所以面板与遮罩都 portal 到 body，top 取打开瞬间顶栏的底边
  const [languagePanelTop, setLanguagePanelTop] = useState(0);
  const openLanguagePanel = () => {
    const bottom = headerRef.current?.getBoundingClientRect().bottom ?? 0;
    setLanguagePanelTop(bottom);
    setLanguagePopoverVisible(true);
  };

  const handleMenuClick = () => {
    if (onMenuClick) {
      onMenuClick();
      return;
    }
    window.dispatchEvent(new CustomEvent(OPEN_LEFT_DRAWER_EVENT));
  };

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const sync = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--header-height", `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // 滚动状态监听 - 使用与 GameBrowser 相同的检测逻辑
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    let container: Element | null = null;
    let rafId: number;

    const checkScrollState = () => {
      const header = headerRef.current;
      if (!header) return;

      const headerBottom = header.getBoundingClientRect().bottom;

      // 方法1：检测 superace-hero-bg 是否滚出 header 区域
      const heroBg = document.querySelector(".superace-hero-bg");
      if (heroBg) {
        const heroBgRect = heroBg.getBoundingClientRect();
        setIsScrolled(heroBgRect.bottom <= headerBottom);
        return;
      }

      // 方法2：检测 categories 是否吸顶（与 GameBrowser 相同的逻辑）
      const categories = document.querySelector(".game-browser__categories");
      if (categories) {
        const catRect = categories.getBoundingClientRect();
        const stuck = catRect.top <= headerBottom + 2;
        setIsScrolled(stuck);
        return;
      }

      // 方法3：直接检测 scrollTop
      if (container && container.scrollTop > 0) {
        setIsScrolled(true);
      }
    };

    const setupListener = () => {
      container = document.querySelector(".mobile-home-page");
      if (container) {
        container.addEventListener("scroll", checkScrollState, {
          passive: true,
        });
        checkScrollState();
        return true;
      }
      return false;
    };

    // 持续尝试设置监听，直到成功
    const trySetup = () => {
      if (!setupListener()) {
        rafId = requestAnimationFrame(trySetup);
      }
    };
    trySetup();

    return () => {
      cancelAnimationFrame(rafId);
      container?.removeEventListener("scroll", checkScrollState);
    };
  }, []);

  const platformLogo = usePlatformLogo();
  const logoSrc = utils.getImageUrl(platformLogo?.logoUrl || "");

  const handleServiceClick = () => {
    if (onServiceClick) {
      onServiceClick();
    } else {
      navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=1`);
    }
  };

  const handleSearchClick = () => {
    if (onSearchClick) {
      onSearchClick();
    } else {
      navigate("/game-search");
    }
  };

  const handleMessageClick = () => {
    if (onMessageClick) {
      onMessageClick();
    } else {
      navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=2`);
    }
  };

  const handlePromoClick = () => {
    if (isAuthenticated) {
      navigate(ROUTES.MESSAGE_CENTER);
    } else {
      openLoginRegisterPopup({ defaultMode: "login" });
    }
  };

  /**
   * 铃铛图标（照设计稿手绘，比 IconSvgMap 的 notification 更圆润：圆角钟身 + 顶部悬挂点 + 底部摆锤弧）。
   * 描边吃**次级按钮色** —— `--color-btn-secondary-start/end`（见 styles/themes/_theme.scss，
   * 与 --color-gradient-secondary 同两档色；SVG 的 stroke 吃不了 linear-gradient()，故拆成两个色标
   * 喂 <linearGradient>），变量缺失时回落 currentColor。
   * 渐变 id 全局唯一即可：一屏最多渲染一个本 variant 的铃铛（首页三槽的右槽 / profile-vip 的右侧区）。
   */
  const renderBellIcon = () => (
    <svg
      className="mobile-header__promo-icon"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke={`url(#${BELL_GRADIENT_ID})`}
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <defs>
        <linearGradient
          id={BELL_GRADIENT_ID}
          x1="12"
          y1="3"
          x2="12"
          y2="21"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="var(--color-btn-secondary-start, currentColor)" />
          <stop
            offset="1"
            stopColor="var(--color-btn-secondary-end, currentColor)"
          />
        </linearGradient>
      </defs>
      {/*
        整体下移 0.7：三段路径的实际占位是 y 2.6~20.03（视觉中心 11.3），
        比 24 见方 viewBox 的中心 12 高了 0.7 —— 元素本身居中了、图形却偏上。
        与其改每段 d，统一在这里平移，形状不动、居中即成立。
      */}
      <g transform="translate(0 0.7)">
        {/* 钟身：圆角肩 + 平底鸣边（下沿两端各带一小段外张） */}
        <path d="M12 4.1c-3.09 0-5.6 2.5-5.6 5.6v2.53c0 .77-.26 1.51-.74 2.1l-.7.87c-.5.62-.06 1.55.73 1.55h12.62c.79 0 1.23-.93.73-1.55l-.7-.87a3.35 3.35 0 0 1-.74-2.1V9.7c0-3.1-2.51-5.6-5.6-5.6Z" />
        {/* 顶部悬挂点 */}
        <path d="M12 4.1V2.6" />
        {/* 底部摆锤弧 */}
        <path d="M10.15 19.05a1.95 1.95 0 0 0 3.7 0" />
      </g>
    </svg>
  );

  /**
   * 右侧铃铛按钮（取代 template3 的信封）：无底色无描边，只有图标本身。
   * 有未读消息时右上角挂角标，复用既有 __badge 样式（>99 显示 99+）。
   */
  const renderBellButton = () => (
    <button
      className="mobile-header__promo-btn"
      aria-label={t("common.messageCenter")}
      onClick={handlePromoClick}
    >
      {renderBellIcon()}
      {messageUnreadCount > 0 && (
        <span className="mobile-header__badge">
          {messageUnreadCount > 99 ? "99+" : messageUnreadCount}
        </span>
      )}
    </button>
  );

  /**
   * 已登录中间槽：余额胶囊 = 左侧「货币符号 + 金额」+ 右侧实心充值按钮。
   * 与 template3 的 .wallet-item 相比去掉了刷新按钮；货币符号走 useCurrencySymbol（跟随国家配置，
   * 不写死 ₱），金额文字用 --color-text-amount（项目定义的金额色）。金额区点击进账户明细。
   */
  const renderBalancePill = () => {
    const balance = userInfo?.balanceDetail?.balance ?? userInfo?.balance ?? 0;

    return (
      <div className="mobile-header__balance-pill">
        <div
          className="mobile-header__balance-info"
          onClick={() => navigate(ROUTES.ACCOUNT_DETAILS)}
        >
          {/* 货币图标优先（后台 startup.currencies[].icon），没配就回落文字符号 */}
          {currencyIcon ? (
            <img
              src={utils.getImageUrl(currencyIcon)}
              alt={currencySymbol}
              className="mobile-header__balance-currency-icon"
              draggable={false}
            />
          ) : (
            <span className="mobile-header__balance-symbol">
              {currencySymbol}
            </span>
          )}
          <span className="mobile-header__balance-amount">
            {/* 千分位与个人中心余额卡一致（原 toFixed 出来是 101159.09） */}
            {balance.toLocaleString("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </span>
        </div>

        {/* 充值按钮：背景走 --btn-primary-bg（primary 变体本身就消费该 token） */}
        <Button
          variant="primary"
          size="sm"
          className="mobile-header__deposit-btn"
          onClick={() => openDepositPopup()}
        >
          {t("deposit.title")}
        </Button>
      </div>
    );
  };

  const renderIcons = () => {
    if (isAuthenticated) {
      switch (pageType) {
        case "profile":
          return (
            <div className="mobile-header__right-icons">
              <button
                className="mobile-header__message-icon"
                onClick={handleMessageClick}
                aria-label={t("common.messageCenter")}
              >
                <img
                  src={ICON_URLS.message}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
                {messageUnreadCount > 0 && (
                  <span className="mobile-header__badge">
                    {messageUnreadCount > 99 ? "99+" : messageUnreadCount}
                  </span>
                )}
              </button>
              <button
                className="mobile-header__service-icon"
                onClick={handleServiceClick}
                aria-label={t("profile.support")}
              >
                <img
                  src={ICON_URLS.service}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button>
            </div>
          );
        case "subpage":
          return (
            <div className="mobile-header__right-icons">
              <button
                className="mobile-header__service-icon"
                onClick={handleServiceClick}
                aria-label={t("profile.support")}
              >
                <img
                  src={ICON_URLS.service}
                  alt=""
                  className="mobile-header__auth-bar-icon-img"
                  draggable={false}
                />
              </button>
            </div>
          );
        case "home":
        default:
          // 首页（home）已登录走 renderSuperAceHomeHeader，不经此处；
          // 这里兜的是 vip 等其它 pageType —— 与首页登录态同款（余额胶囊 + 铃铛）
          return (
            <>
              {renderBalancePill()}
              {renderBellButton()}
            </>
          );
      }
    }

    // 未登录状态
    if (pageType === "profile") {
      // profile 页面未登录时：不显示登录按钮，显示语言、消息、客服 icon
      return (
        <div className="mobile-header__right-icons">
          <button
            className="mobile-header__language-icon"
            onClick={onLanguageClick}
            aria-label={t("profile.language")}
          >
            <img
              src={ICON_URLS.language}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
          <button
            className="mobile-header__message-icon"
            onClick={handleMessageClick}
            aria-label={t("common.messageCenter")}
          >
            <img
              src={ICON_URLS.message}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
          <button
            className="mobile-header__service-icon"
            onClick={handleServiceClick}
            aria-label={t("profile.support")}
          >
            <img
              src={ICON_URLS.service}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
        </div>
      );
    }

    // 其他页面未登录时：显示登录按钮 + 搜索、客服 icon
    return (
      <>
        <Button
          variant="primary"
          size="sm"
          className="mobile-header__register-login-btn"
          onClick={() => openLoginRegisterPopup({ defaultMode: "register" })}
        >
          {t("auth.registerLogin")}
        </Button>
        <div className="mobile-header__right-icons">
          <button
            className="mobile-header__search-icon"
            onClick={handleSearchClick}
            aria-label="Search"
          >
            <img
              src={ICON_URLS.search}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
          <button
            className="mobile-header__service-icon"
            onClick={handleServiceClick}
            aria-label={t("profile.support")}
          >
            <img
              src={ICON_URLS.service}
              alt=""
              className="mobile-header__auth-bar-icon-img"
              draggable={false}
            />
          </button>
        </div>
      </>
    );
  };

  // 下载条 + 等高占位块（仅首页）。DownloadAppBar 自带路由/会话/App 三重显隐门；
  // __download-slot 在浮动顶栏内把 header 内容让到下载条下方（与 classic variant 同构）。
  //
  // 占位块必须再自己判一次 isHome()，不能只信 downloadAppBarVisible：profile=template4 时
  // Layout 给个人中心也传 pageType="home"（要同款头），于是这里在 /profile 上也会走一遍。
  // 而 downloadAppBarVisible 的初值是「本会话没关过下载条」，同步的 false 只能靠
  // DownloadAppBar 派发 downloadAppBarChange 事件送达 —— 它是本组件的子节点，子 effect 先于
  // 父 effect 执行，直接刷新 /profile 时事件在监听器注册前就发完了，占位块留下一截 48px 空白。
  const renderDownloadBar = () => (
    <>
      <DownloadAppBar />
      {downloadAppBarVisible && isHome() && (
        <div className="mobile-header__download-slot" aria-hidden="true" />
      )}
    </>
  );

  /**
   * 首页布局（登录前后共用）：左 ☰ / 左对齐 logo / 右侧动作区（登录注册 or 余额胶囊）+ 地球。
   * flex 一行：logo 紧挨 ☰ 左对齐并吃掉剩余宽度，动作区靠右（竞品 logo 不居中）。
   */
  const renderSuperAceHomeHeader = () => {
    const balance = userInfo?.balanceDetail?.balance ?? userInfo?.balance ?? 0;
    return (
      <div className="mobile-header__sg">
        {/* 左：汉堡菜单 */}
        <button
          className="mobile-header__sg-menu"
          onClick={handleMenuClick}
          aria-label={t("navigation.menu")}
        >
          {/* 竞品同款字形（可后台换图：皮肤图标 header.menu） */}
          <SkinIcon
            name="header.menu"
            className="mobile-header__sg-menu-icon"
          />
        </button>

        {/* 中：logo（后台平台 logo，左对齐） */}
        <div
          className="mobile-header__sg-logo"
          onClick={() => navigate(ROUTES.HOME)}
        >
          {logoSrc && (
            <img
              src={logoSrc}
              alt="Logo"
              className="mobile-header__sg-logo-img"
              draggable={false}
            />
          )}
        </div>

        {/* 右：登录注册 / 余额胶囊 + 地球 */}
        <div className="mobile-header__sg-actions">
          {isAuthenticated ? (
            <button
              className="mobile-header__sg-balance"
              onClick={() => openDepositPopup()}
              aria-label={t("deposit.title")}
            >
              {/* 竞品 .assets-box：货币用符号文字（p.currency #df8644），不用货币图标 */}
              <span className="mobile-header__sg-balance-symbol">
                {currencySymbol}
              </span>
              <span className="mobile-header__sg-balance-amount">
                {balance.toLocaleString("pt-BR", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </span>
              <span className="mobile-header__sg-piggy">
                <SkinIcon
                  name="header.wallet"
                  className="mobile-header__sg-piggy-icon"
                />
              </span>
            </button>
          ) : (
            <div className="mobile-header__sg-auth">
              <button
                className="mobile-header__sg-login"
                onClick={() => openLoginRegisterPopup({ defaultMode: "login" })}
              >
                {t("auth.login")}
              </button>
              <div className="mobile-header__sg-register-wrap">
                <Button
                  variant="primary"
                  size="sm"
                  className="mobile-header__sg-register"
                  onClick={() =>
                    openLoginRegisterPopup({ defaultMode: "register" })
                  }
                >
                  {t("auth.register")}
                </Button>
                {/* 首充优惠文案：悬浮在注册按钮正下方（后台「首页注册按钮优惠文案」） */}
                {registerPromoText && (
                  <span className="mobile-header__signup-promo">
                    {registerPromoText}
                  </span>
                )}
              </div>
            </div>
          )}

          <button
            className="mobile-header__sg-globe"
            onClick={() => {
              onLanguageClick?.();
              openLanguagePanel();
            }}
            aria-label={t("profile.language")}
          >
            <SkinIcon
              name="header.language"
              className="mobile-header__sg-globe-icon"
            />
          </button>
          {languagePopoverVisible &&
            createPortal(
              <>
                <div
                  className="mobile-header__sg-language-mask"
                  onClick={() => setLanguagePopoverVisible(false)}
                />
                <ul
                  className="mobile-header__sg-language-panel"
                  style={{ top: `calc(${languagePanelTop}px + 1rem)` }}
                  role="menu"
                >
                  {validLanguages.map(id => (
                    <li
                      key={id}
                      role="menuitem"
                      className={`mobile-header__sg-language-item${id === currentLanguage ? " mobile-header__sg-language-item--active" : ""}`}
                      onClick={() => {
                        setLanguagePopoverVisible(false);
                        if (id !== currentLanguage) {
                          void changeLanguage(id);
                        }
                      }}
                    >
                      <LanguageFlag id={id} />
                      {languageLabel(id)}
                    </li>
                  ))}
                </ul>
              </>,
              document.body
            )}
        </div>
      </div>
    );
  };

  // 子页面使用简化的 header（返回按钮 + 标题 + 右侧icon）
  if (pageType === "subpage") {
    return (
      <header
        ref={headerRef}
        className={`mobile-header mobile-header--subpage ${className}`}
      >
        <div className="mobile-header__subpage">
          {/* 左侧：返回按钮或占位 */}
          {!hideBackButton ? (
            <button
              className="mobile-header__back-btn"
              onClick={() => routerNavigate(-1)}
              aria-label="Go back"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 36 30"
                stroke="currentColor"
                strokeWidth="4.5"
                className="mobile-header__back-icon"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3 15h30M15 3 3 15l12 12"
                />
              </svg>
            </button>
          ) : (
            <div className="mobile-header__back-placeholder" />
          )}
          {/* 中间：标题 */}
          <h1 className="mobile-header__subpage-title">{title}</h1>
          {/* 右侧：icon或占位 */}
          {rightIcon ? (
            <div className="mobile-header__right-icon">{rightIcon}</div>
          ) : (
            <div className="mobile-header__right-placeholder" />
          )}
        </div>
      </header>
    );
  }

  // 首页恒用三槽布局（与 template3 不同：登录后也走这里，不再切到 __auth-bar 那套）。
  // 不叠 --authenticated：那个修饰符改的是 __auth-bar 那套的高度/内边距，与 --superace 冲突。
  if (pageType === "home") {
    return (
      <>
        {renderDownloadBar()}
        <header
          ref={headerRef}
          className={`mobile-header mobile-header--superace ${isScrolled ? "mobile-header--scrolled" : ""} ${className}`}
        >
          {renderSuperAceHomeHeader()}
        </header>
      </>
    );
  }

  // 非首页、非子页面（profile / vip）：沿用 template3 的 __auth-bar 布局
  // （home 已在上面提前返回，故这里不必再判 pageType === "home"）
  return (
    <>
      <header
        ref={headerRef}
        className={`mobile-header mobile-header--${pageType} ${isAuthenticated ? "mobile-header--authenticated" : ""} ${className}`}
      >
        <div className="mobile-header__content">
          <div className="mobile-header__auth-bar">
            <div className="mobile-header__logo">
              {logoSrc && (
                <img
                  src={logoSrc}
                  alt="Logo"
                  className="mobile-header__logo-img"
                  draggable={false}
                />
              )}
            </div>

            <div className="mobile-header__right-section">{renderIcons()}</div>
          </div>
        </div>
      </header>
    </>
  );
}

export default Header;
